/**
 * glør Native Variant Speed — core-freie DESKTOP-Beschleunigung des nativen
 * Horizon-Varianten-Pickers.
 *
 * Problem: Auf Desktop bedient der native <variant-picker> den Wechsel; er holt
 * bei jedem Klick die Section per Fetch und morpht sie → Preis und Bild springen
 * erst nach dem Roundtrip um. Der mobile Schnellwähler (glr-variant-quick) macht
 * bereits ein optimistisches Preis-Update, ist aber `mobile_only`.
 *
 * Diese Datei überbrückt genau diese Wartezeit auf DESKTOP, rein additiv:
 *  1) OPTIMISTISCHES PREIS-UPDATE: bei Auswahländerung am nativen Picker sofort
 *     den sichtbaren Preis aus der lokalen Varianten-Matrix schreiben — exakt in
 *     dieselben <product-price>-Ziele und mit derselben Logik wie
 *     glr-variant-quick.js#updatePrice (formatierte `| money`-Strings aus der
 *     Matrix, kein Float, keine Rundung im JS). Der native Fetch-and-Morph läuft
 *     unverändert weiter und „bestätigt" Preis/Bild danach idempotent.
 *  2) PREFETCH/CACHE-WARMING ist NICHT hier — das erledigt bereits
 *     glr-variant-prefetch.js (Hover/Focus/Touch am nativen Picker, identische
 *     ?option_values=…-URL). Kein Doppel-Prefetch.
 *  3) BILD-TAUSCH ist bewusst NICHT umgesetzt: der Gallery-Umbau liegt in
 *     Horizon-Core (product-media) und ist beim Morph fragil — „lieber defensiv
 *     nichts tun als das Layout riskieren". Der native Morph tauscht das Bild.
 *
 * Kollisionsschutz mit dem mobilen glr-variant-quick: dieses Skript aktiviert
 * das optimistische Update NUR im Desktop-Viewport (min-width: 750px — derselbe
 * Breakpoint, ab dem .glr-vq--mobile-only ausgeblendet wird). Zusätzlich ist das
 * Schreiben idempotent (setzt nur denselben formatierten String), sodass es
 * selbst bei Überschneidung nichts kaputt macht.
 *
 * Matrix-Quelle: `[data-glr-variant-options]` aus glr-pdp-availability.liquid
 * (id, options[], available, price, compare). Dieselbe Matrix, die auch
 * glr-native-preorder-badge liest — nur um price/compare erweitert.
 *
 * KEIN Eingriff in Horizon-Core (weder variant-picker* noch product-price* noch
 * product-media*). Nur Lesen der nativen Radios und Schreiben in die sichtbaren
 * Preis-Spans.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 750px)';

  function isDesktop() {
    try {
      return window.matchMedia(DESKTOP_MQ).matches;
    } catch (e) {
      return true; // im Zweifel wie Desktop behandeln (keine Regression ggü. nativ)
    }
  }

  // ---- Matrix laden (dieselbe Quelle wie glr-native-preorder-badge) ----------
  function loadMatrix() {
    var el = document.querySelector('[data-glr-variant-options]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]') || [];
    } catch (e) {
      return [];
    }
  }

  var matrix = loadMatrix();
  if (!matrix.length || !matrix[0] || !matrix[0].options) return;
  // Ohne formatierte Preise in der Matrix gibt es nichts zu beschleunigen.
  if (matrix[0].price == null || matrix[0].price === '') return;
  var optCount = matrix[0].options.length;

  // Produkt-ID des Haupt-Pickers (zum Filtern der richtigen Preis-Elemente).
  function mainProductId() {
    var picker = document.querySelector('variant-picker');
    return (picker && picker.dataset && picker.dataset.productId) || '';
  }
  var PRODUCT_ID = mainProductId();

  // ---- Aktuelle Auswahl am nativen Picker bestimmen --------------------------
  // Unterstützt Radios (Swatches/Pills, Standardfall) UND Select-Dropdowns.
  // Gibt Gruppen mit { values:[…alle möglichen Werte…], current:string|null } zurück.
  function readGroups(picker) {
    var groups = [];

    // Radios nach name-Attribut bündeln (DOM-Reihenfolge).
    var radios = Array.prototype.slice.call(picker.querySelectorAll('input[type="radio"]'));
    var order = [];
    var byName = {};
    radios.forEach(function (r) {
      var n = r.name || '';
      if (!byName[n]) {
        byName[n] = { values: [], current: null };
        order.push(byName[n]);
      }
      var val = (r.value || '').trim();
      byName[n].values.push(val);
      if (r.checked) byName[n].current = val;
    });
    order.forEach(function (g) { groups.push(g); });

    // Select-Dropdowns (Fallback-Layout des nativen Pickers).
    var selects = Array.prototype.slice.call(picker.querySelectorAll('select'));
    selects.forEach(function (sel) {
      var g = { values: [], current: null };
      Array.prototype.slice.call(sel.options).forEach(function (o) {
        g.values.push((o.value || '').trim());
      });
      g.current = (sel.value || '').trim() || null;
      groups.push(g);
    });

    return groups;
  }

  // Jeder Gruppe ihre Position in der Matrix-options[] zuordnen — über
  // Wert-Zugehörigkeit (die Werte-Spalte p enthält nur die Werte dieser Option).
  function assignPositions(groups) {
    var pos = new Map();
    var used = {};
    groups.forEach(function (g) {
      var set = {};
      g.values.forEach(function (v) { set[v] = true; });
      for (var p = 0; p < optCount; p++) {
        if (used[p]) continue;
        var fits = matrix.every(function (variant) {
          return set[String(variant.options[p]).trim()] === true;
        });
        if (fits) {
          pos.set(g, p);
          used[p] = true;
          break;
        }
      }
    });
    return pos;
  }

  // Aktuell gewählte Werte je Matrix-Position (null = unbestimmt/Wildcard).
  function currentValues(groups, positions) {
    var vals = new Array(optCount).fill(null);
    groups.forEach(function (g) {
      var p = positions.get(g);
      if (p == null) return;
      if (g.current != null) vals[p] = g.current;
    });
    return vals;
  }

  function variantFromValues(vals) {
    return matrix.filter(function (v) {
      return vals.every(function (want, i) {
        return want == null || String(v.options[i]).trim() === want;
      });
    })[0];
  }

  function currentVariant(picker) {
    var groups = readGroups(picker);
    if (!groups.length) return null;
    var positions = assignPositions(groups);
    var vals = currentValues(groups, positions);
    return variantFromValues(vals) || null;
  }

  // ---- Preis schreiben (1:1 wie glr-variant-quick.js#updatePrice/#priceElements)
  function priceElements() {
    var out = [];
    var list = document.querySelectorAll('product-price');
    for (var i = 0; i < list.length; i++) {
      var pp = list[i];
      if (!(pp instanceof HTMLElement)) continue;
      if (pp.closest('product-card')) continue; // Empfehlungen/Cards ausklammern
      if (PRODUCT_ID && pp.dataset.productId && pp.dataset.productId !== PRODUCT_ID) continue;
      out.push(pp);
    }
    return out;
  }

  function updatePrice(variant) {
    if (!variant || variant.price == null || variant.price === '') return;
    var hasCompare = !!variant.compare;
    var els = priceElements();
    for (var i = 0; i < els.length; i++) {
      var pp = els[i];
      var container = pp.querySelector('[ref="priceContainer"]') || pp;
      // Aktueller Preis: alle .price-Spans außer den Streichpreis-Spans.
      var prices = container.querySelectorAll('.price');
      for (var j = 0; j < prices.length; j++) {
        if (!prices[j].classList.contains('compare-at-price')) {
          if (prices[j].textContent !== variant.price) prices[j].textContent = variant.price;
        }
      }
      // Streichpreis nur setzen, wenn vorhanden (sonst alten Wert nicht anfassen).
      if (hasCompare) {
        var cmp = container.querySelectorAll('.compare-at-price');
        for (var k = 0; k < cmp.length; k++) {
          if (cmp[k].textContent !== variant.compare) cmp[k].textContent = variant.compare;
        }
      }
      // Regulär-/Sale-Layout umschalten (wie im Preis-Snippet: price__hidden).
      var regular = container.querySelector('.price__regular');
      var sale = container.querySelector('.price__sale');
      if (regular && sale) {
        regular.classList.toggle('price__hidden', hasCompare);
        sale.classList.toggle('price__hidden', !hasCompare);
      }
    }
  }

  function optimisticUpdate(picker) {
    if (!isDesktop()) return; // Mobil erledigt das glr-variant-quick
    if (!picker) return;
    var variant = currentVariant(picker);
    if (variant) updatePrice(variant);
  }

  // ---- Verdrahtung -----------------------------------------------------------
  function boot() {
    // Auswahländerung am nativen Picker (Radios + Selects).
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('variant-picker input[type="radio"]') || t.matches('variant-picker select')) {
        var picker = t.closest('variant-picker');
        if (picker) optimisticUpdate(picker);
      }
    });

    // Standard-Shopify-Event: greift auch, wenn der Wechsel nicht über ein
    // change-Event lief. Der native Morph reconciled danach ohnehin.
    document.addEventListener('shopify:product:select', function (e) {
      var picker = (e && e.target && e.target.closest && e.target.closest('variant-picker')) ||
        document.querySelector('variant-picker');
      optimisticUpdate(picker);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
